/**
 * useReservationRelease.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Liberar o horário de uma reserva pendente, com o aviso opcional pelo
 * WhatsApp. Mesma regra no painel da agenda (ReservationPanel) e na aba
 * Reservas do Início (ReservationItem).
 *
 * Com número próprio a mensagem sai pelo sistema (reservations/notify_released.php)
 * depois de excluir o bloqueio; sem ele abre o WhatsApp com a mensagem pronta
 * no próprio clique, para o navegador não bloquear a janela.
 *
 * @module useReservationRelease
 */

import { useState, useCallback } from 'react';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import { buildReleasedMessage, parseReservationDate, reservationWhatsAppUrl } from '../../utils/reservationMessage';

/**
 * @param {Object}   params
 * @param {number}   params.appointmentId - bloqueio da reserva
 * @param {Object}   params.data - resposta de reservations/get.php ({ reservation, own_number })
 * @param {Function} params.onReleased - horário liberado (e aviso enviado, quando pedido)
 */
export default function useReservationRelease({ appointmentId, data, onReleased }) {
  const { DeleteAppointment, NotifyReleasedAppointmentReservation, CompanyConfig } = DataProviderInstance();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [notifyPatient, setNotifyPatient] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  // Horário já liberado, mas o aviso pelo número próprio falhou: oferece abrir o WhatsApp
  const [noticeFailed, setNoticeFailed] = useState(false);

  const reservation = data?.reservation;
  const ownNumber = !!data?.own_number;
  // Aviso só faz sentido para horário que ainda não passou
  const canNotify = !!reservation && parseReservationDate(reservation.date) > new Date();
  const releasedMessage = canNotify ? buildReleasedMessage(reservation, CompanyConfig) : '';

  const openReleasedInWhatsApp = useCallback(() => {
    window.open(reservationWhatsAppUrl(reservation, releasedMessage), '_blank', 'noopener,noreferrer');
  }, [reservation, releasedMessage]);

  const release = useCallback(() => {
    const notify = canNotify && notifyPatient;
    // Sem número próprio: abre o WhatsApp já no clique (depois do await o navegador bloqueia)
    if (notify && !ownNumber) openReleasedInWhatsApp();

    setIsBusy(true);
    DeleteAppointment(appointmentId, () => {
      setConfirmOpen(false);
      if (!notify || !ownNumber) {
        setIsBusy(false);
        onReleased?.();
        return;
      }
      NotifyReleasedAppointmentReservation(appointmentId, (res) => {
        setIsBusy(false);
        if (res?.success) {
          onReleased?.();
          return;
        }
        setNoticeFailed(true);
      });
    });
  }, [appointmentId, canNotify, notifyPatient, ownNumber, DeleteAppointment, NotifyReleasedAppointmentReservation, openReleasedInWhatsApp, onReleased]);

  const openConfirm = useCallback(() => setConfirmOpen(true), []);
  const closeConfirm = useCallback(() => setConfirmOpen(false), []);

  /** Volta ao estado inicial (ao abrir o painel de novo). */
  const reset = useCallback(() => {
    setConfirmOpen(false);
    setNotifyPatient(true);
    setIsBusy(false);
    setNoticeFailed(false);
  }, []);

  return {
    confirmOpen, openConfirm, closeConfirm,
    notifyPatient, setNotifyPatient, canNotify, ownNumber,
    isBusy, noticeFailed,
    release, openReleasedInWhatsApp, reset,
  };
}
